import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faChevronRight, faChevronLeft, faPaperPlane, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { toastHelper } from '../../../Helpers/toastHelper';
import ApiServices from '../../Services/ApiServices';
import MiniLoader from '../MiniLoader';
import { useLanguage } from '../../../contexts/LanguageContext';
import './DynamicStepper.css';

/**
 * DynamicStepper Component
 * 
 * @param {Array} steps - Array of objects: { id, title, description, component: ReactNode, apiEndpoint?: string }
 * @param {String} submissionMode - 'step-by-step' (each step has an apiEndpoint) or 'all-at-once' (one final API call)
 * @param {String} finalApiEndpoint - URL used when submissionMode is 'all-at-once'
 * @param {Function} onComplete - Callback executed when stepper is successfully completed
 */
const DynamicStepper = ({
  steps = [],
  submissionMode = 'all-at-once',
  finalApiEndpoint = '',
  isAITheme = false,
  isExternalProcessing = false,
  onComplete = () => {}
}) => {
  const { t, language } = useLanguage();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // State to gather all data from steps
  const [formData, setFormData] = useState({});

  if (!steps || steps.length === 0) {
    return <div className="stepper-glass-panel text-center p-4">{t('ds_noSteps')}</div>;
  }

  const currentStep = steps[currentStepIndex];
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === steps.length - 1;

  // Handle data updates from child step components
  const updateFormData = (stepId, data) => {
    setFormData(prev => ({
      ...prev,
      [stepId]: { ...prev[stepId], ...data }
    }));
  };

  const handleNext = async () => {
    // If we are in step-by-step mode and this step has an API endpoint, call it
    if (submissionMode === 'step-by-step' && currentStep.apiEndpoint) {
      try {
        setIsProcessing(true);
        const stepData = formData[currentStep.id] || {};
        const response = await ApiServices.post(currentStep.apiEndpoint, stepData);
        if (response && response.status !== false && !response.error) {
          // Success
          moveToNextStep();
        } else {
          toastHelper.error(response?.message || t('ds_failedSave'));
        }
      } catch (error) {
        toastHelper.error(t('ds_unexpectedError'));
      } finally {
        setIsProcessing(false);
      }
    } else {
      moveToNextStep();
    }
  };

  const moveToNextStep = () => {
    if (!isLastStep) {
      setCurrentStepIndex(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (!isFirstStep) {
      setCurrentStepIndex(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    if (submissionMode === 'all-at-once' && finalApiEndpoint) {
      try {
        setIsProcessing(true);
        
        // Flatten or format formData as needed. For now, we'll send it as a grouped object.
        // E.g., { step1: {name: 'John'}, step2: {age: 30} }
        const payload = Object.values(formData).reduce((acc, curr) => ({ ...acc, ...curr }), {});

        const response = await ApiServices.post(finalApiEndpoint, payload);
        if (response && response.status !== false && !response.error) {
          toastHelper.success(t('ds_submitSuccess'));
          onComplete(response);
        } else {
          toastHelper.error(response?.message || t('ds_submitFailed'));
        }
      } catch (error) {
        toastHelper.error(t('ds_unexpectedError'));
      } finally {
        setIsProcessing(false);
      }
    } else if (submissionMode === 'step-by-step' && currentStep.apiEndpoint) {
      // If it's step-by-step, the last step also needs its API call
      await handleNext();
      if (!isProcessing) { // Assuming success if not processing and no error thrown
        toastHelper.success(t('ds_completedSuccess'));
        onComplete(formData);
      }
    } else {
      // No API needed, just complete
      if (submissionMode !== 'manual') {
        toastHelper.success(t('ds_completedSuccess'));
      }
      onComplete(formData);
    }
  };

  // Calculate progress bar width
  const progressPercentage = (currentStepIndex / (steps.length - 1)) * 100;

  return (
    <div className={`dynamic-stepper-wrapper ${isAITheme ? 'ai-stepper-theme' : ''}`}>
      <div className="stepper-glass-panel">
        
        {/* Global Loading Overlay */}
        <AnimatePresence>
          {isProcessing && (
            <motion.div 
              className="global-loading-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <FontAwesomeIcon icon={faSpinner} spin size="3x" color="#6366f1" />
              <span>{t('ds_processing')}</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Header / Progress Indicators */}
        <div className="stepper-header">
          <div className="stepper-progress-fill" style={{ width: `${progressPercentage}%` }}></div>
          {steps.map((step, index) => {
            const isCompleted = index < currentStepIndex;
            const isActive = index === currentStepIndex;
            
            return (
              <div 
                key={step.id || index} 
                className={`step-indicator-wrapper ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
              >
                <div className="step-circle">
                  {isCompleted ? <FontAwesomeIcon icon={faCheck} /> : (index + 1)}
                </div>
                <span className="step-title">{step.title}</span>
              </div>
            );
          })}
        </div>

        {/* Step Content */}
        <div className="step-content-area">
          <div className="step-content-header">
            <h2>{currentStep.title}</h2>
            {currentStep.description && <p>{currentStep.description}</p>}
          </div>

          <div className="step-component-container">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStepIndex}
                initial={{ opacity: 0, x: language === 'ar' ? -30 : 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: language === 'ar' ? 30 : -30 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
              >
                {/* Clone the element to inject the update hook and current step data */}
                {React.isValidElement(currentStep.component) 
                  ? React.cloneElement(currentStep.component, {
                      stepData: formData[currentStep.id] || {},
                      updateData: (data) => updateFormData(currentStep.id, data),
                    })
                  : currentStep.component}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="stepper-actions">
          <button 
            type="button" 
            className="btn-stepper btn-back" 
            onClick={handleBack}
            disabled={isFirstStep || isProcessing}
          >
            <FontAwesomeIcon icon={language === 'ar' ? faChevronRight : faChevronLeft} />
            {t('ds_btnBack')}
          </button>

          {isLastStep ? (
            <button 
              type="button" 
              className="btn-stepper btn-submit" 
              onClick={handleSubmit}
              disabled={isProcessing || isExternalProcessing}
            >
              {(isProcessing || isExternalProcessing) ? (
                <MiniLoader color="#fff" size={20} />
              ) : (
                <FontAwesomeIcon icon={faPaperPlane} />
              )}
              {t('ds_btnSubmit')}
            </button>
          ) : (
            <button 
              type="button" 
              className="btn-stepper btn-next" 
              onClick={handleNext}
              disabled={isProcessing}
            >
              {t('ds_btnNext')}
              <FontAwesomeIcon icon={language === 'ar' ? faChevronLeft : faChevronRight} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DynamicStepper;
